/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { FAQsModule } from '@coursera-web/cds-labs';

import type { PromoTemplateFaqsFragment } from 'bundles/promo-template/__generated__/contentfulTypes';
import Markdown from 'bundles/promo-template/contentful/core/Markdown';
import FaqsSchemaMarkup from 'bundles/seo/components/FaqsSchemaMarkup';
import HelpCenterInfo from 'bundles/unified-common/components/HelpCenterInfo';

export type Props = {
  content: PromoTemplateFaqsFragment;
};

const styles = {
  markdown: css`
    // we want to display unordered lists as bullets, so we need to
    // override the styles defined in sharedMarkdownStyles
    ul {
      padding-left: var(--cds-spacing-400);

      li {
        display: list-item;
        list-style-type: disc;
        padding-left: var(--cds-spacing-100);
      }
    }
  `,
};

function FAQsModuleRenderer({ content }: Props) {
  const faqs = useMemo(
    () =>
      content.faqsCollection?.items
        .map((faq) => {
          if (!faq || !faq.question || !faq.answer) return false;

          return {
            question: faq.question,
            plainTextAnswer: faq.answer,
            answer: <Markdown source={faq.answer} module={content} css={styles.markdown} />,
          };
        })
        .filter(Boolean),
    [content]
  );

  if (!faqs?.length) return null;

  return (
    <>
      <FAQsModule
        {...content}
        headline={content.headline}
        faqs={faqs}
        moreInfo={content.showHelpCenter ? <HelpCenterInfo /> : null}
      />
      <FaqsSchemaMarkup faqs={faqs.map(({ question, plainTextAnswer }) => ({ question, answer: plainTextAnswer }))} />
    </>
  );
}

export default FAQsModuleRenderer;
